import { useDroppable } from '@dnd-kit/core';
import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import { DESIGN_CANVAS, useCanvasScale } from '@/lib/canvas';
import type { Column as ColumnType, Row as RowType } from '@/lib/schema.types';
import { cn } from '@/lib/utils';
import { useEditorActions, useEditorStore } from '@/store/editor.store';

import { FreeformElement } from './FreeformElement';
import { FreeformGroupProvider, useFreeformGroupMaybe } from './FreeformGroupContext';
import { FreeformSnapContext, type SnapGuides } from './FreeformSnapContext';

// Freeform rows live in the shared design canvas: `heightPercent` is a % of the
// full canvas height. A row can span the whole 16:9 frame.
const SLIDE_HEIGHT_PX = DESIGN_CANVAS.height;
const MIN_HEIGHT_PX = 120;
const MAX_HEIGHT_PX = SLIDE_HEIGHT_PX;

export function clampPercent(p: number) {
  return Math.min(100, Math.max(15, Math.round(p * 10) / 10));
}

export function rowHeightPx(row: RowType): number {
  return Math.max(MIN_HEIGHT_PX, (row.heightPercent / 100) * SLIDE_HEIGHT_PX);
}

/** Single-column freeform row — owns its own bottom resize handle. */
export function FreeformRow({ row, slideId }: { row: RowType; slideId: string }) {
  const column = row.columns[0];
  if (!column) return null;
  return (
    <FreeformGroupProvider>
      <FreeformColumnSurfaceInner slideId={slideId} row={row} column={column} showBottomResize />
    </FreeformGroupProvider>
  );
}

/**
 * One freeform canvas for a single column. Multi-column rows render one of these
 * per column with a shared, parent-controlled height (`forcedHeightPx`).
 */
export function FreeformColumnSurface({
  slideId,
  row,
  column,
  forcedHeightPx,
}: {
  slideId: string;
  row: RowType;
  column: ColumnType;
  forcedHeightPx: number;
}) {
  return (
    <FreeformGroupProvider>
      <FreeformColumnSurfaceInner
        slideId={slideId}
        row={row}
        column={column}
        showBottomResize={false}
        forcedHeightPx={forcedHeightPx}
      />
    </FreeformGroupProvider>
  );
}

/** Shared bottom resize bar for multi-column rows (updates `row.heightPercent`). */
export function RowSharedHeightResizeBar({
  slideId,
  row,
  heightPx,
  onLiveHeight,
}: {
  slideId: string;
  row: RowType;
  heightPx: number;
  onLiveHeight: (px: number | null) => void;
}) {
  const scale = useCanvasScale();
  const actions = useEditorActions();
  const dragRef = useRef<{ startY: number; startH: number } | null>(null);
  const liveRef = useRef<number | null>(null);

  const beginHeightDrag = (e: ReactPointerEvent<HTMLSpanElement>) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    dragRef.current = { startY: e.clientY, startH: heightPx };
    const onMove = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      ev.preventDefault();
      const next = Math.min(
        MAX_HEIGHT_PX,
        Math.max(MIN_HEIGHT_PX, d.startH + (ev.clientY - d.startY) / scale),
      );
      liveRef.current = next;
      onLiveHeight(next);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      const latest = liveRef.current ?? heightPx;
      liveRef.current = null;
      dragRef.current = null;
      const nextPercent = clampPercent((latest / SLIDE_HEIGHT_PX) * 100);
      if (Math.abs(nextPercent - row.heightPercent) >= 0.5) {
        actions.updateRow(slideId, row.id, { heightPercent: nextPercent });
      }
      onLiveHeight(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  return (
    <span
      onPointerDown={beginHeightDrag}
      title="Drag to resize row height"
      className="mt-1 flex h-2 w-full cursor-ns-resize items-center justify-center rounded-sm opacity-0 transition group-hover/row:opacity-100"
    >
      <span className="h-1 w-16 rounded-full bg-border" />
    </span>
  );
}

function FreeformColumnSurfaceInner({
  slideId,
  row,
  column,
  showBottomResize,
  forcedHeightPx,
}: {
  slideId: string;
  row: RowType;
  column: ColumnType;
  showBottomResize: boolean;
  forcedHeightPx?: number;
}) {
  const scale = useCanvasScale();
  const actions = useEditorActions();

  const { isOver, setNodeRef } = useDroppable({
    id: `col:${slideId}:${row.id}:${column.id}`,
    data: { kind: 'column', slideId, rowId: row.id, columnId: column.id },
  });

  const baseHeightPx = rowHeightPx(row);
  const [snapGuides, setSnapGuides] = useState<SnapGuides>([]);
  const [liveHeightPx, setLiveHeightPx] = useState<number | null>(null);
  const heightPx = forcedHeightPx ?? liveHeightPx ?? baseHeightPx;
  const isResizing = showBottomResize && liveHeightPx != null;

  const isSelected = useEditorStore(
    (s) =>
      s.selection?.kind === 'column' &&
      s.selection.columnId === column.id &&
      s.selection.rowId === row.id,
  );

  const dragRef = useRef<{ startY: number; startH: number } | null>(null);
  const liveHeightPxRef = useRef<number | null>(null);
  liveHeightPxRef.current = liveHeightPx;

  const beginHeightDrag = (e: ReactPointerEvent<HTMLSpanElement>) => {
    if (!showBottomResize || e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    dragRef.current = { startY: e.clientY, startH: baseHeightPx };
    const onMove = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      ev.preventDefault();
      const next = Math.min(
        MAX_HEIGHT_PX,
        Math.max(MIN_HEIGHT_PX, d.startH + (ev.clientY - d.startY) / scale),
      );
      setLiveHeightPx(next);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      const latest = liveHeightPxRef.current ?? baseHeightPx;
      dragRef.current = null;
      setLiveHeightPx(null);
      const nextPercent = clampPercent((latest / SLIDE_HEIGHT_PX) * 100);
      if (Math.abs(nextPercent - row.heightPercent) >= 0.5) {
        actions.updateRow(slideId, row.id, { heightPercent: nextPercent });
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  return (
    <div
      ref={setNodeRef}
      onClick={(e) => {
        e.stopPropagation();
        useEditorStore
          .getState()
          .setSelection({ kind: 'column', slideId, rowId: row.id, columnId: column.id });
      }}
      style={{ height: heightPx }}
      className={cn(
        'group/freeform relative w-full overflow-hidden rounded-md border border-dashed border-border-subtle bg-bg-subtle/30 canvas-grid transition',
        isOver && 'border-accent bg-accent-soft/30',
        isSelected && !isOver && 'border-accent/50',
        isResizing && 'border-accent',
      )}
    >
      {column.elements.length === 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-fg-subtle/70">
          Canvas — drag a block in and place it anywhere
        </div>
      )}

      <FreeformSnapContext.Provider value={{ setGuides: setSnapGuides }}>
        {column.elements.map((element, i) => (
          <FreeformElement
            key={element.id}
            element={element}
            slideId={slideId}
            rowId={row.id}
            columnId={column.id}
            stackIndex={i}
          />
        ))}
      </FreeformSnapContext.Provider>

      {/* Alignment guides (shown only while an element snaps mid-drag). */}
      {snapGuides.map((g, i) => (
        <div
          key={i}
          className="pointer-events-none absolute z-40 bg-accent"
          style={
            g.axis === 'v'
              ? { left: g.pos, top: g.start, width: 1, height: Math.max(1, g.end - g.start) }
              : { top: g.pos, left: g.start, height: 1, width: Math.max(1, g.end - g.start) }
          }
        />
      ))}

      <GroupActionBar slideId={slideId} rowId={row.id} columnId={column.id} />

      {showBottomResize && (
        <>
          <span
            onPointerDown={beginHeightDrag}
            title="Drag to resize canvas height"
            className={cn(
              'absolute inset-x-0 bottom-0 z-30 flex h-2 cursor-ns-resize items-center justify-center',
              'opacity-0 transition group-hover/freeform:opacity-100',
              (isSelected || isResizing) && 'opacity-100',
            )}
          >
            <span
              className={cn(
                'h-1 w-16 rounded-full bg-border transition',
                (isResizing || isSelected) && 'bg-accent',
              )}
            />
          </span>
          {isResizing && (
            <span className="pointer-events-none absolute bottom-1 right-2 z-30 rounded-md bg-fg/80 px-2 py-0.5 text-[10px] font-medium text-white shadow-pill">
              {Math.round((heightPx / SLIDE_HEIGHT_PX) * 100)}%
            </span>
          )}
        </>
      )}
    </div>
  );
}

/** Floating bar shown while 2+ freeform elements are multi-selected for grouping. */
function GroupActionBar({
  slideId,
  rowId,
  columnId,
}: {
  slideId: string;
  rowId: string;
  columnId: string;
}) {
  const group = useFreeformGroupMaybe();
  const actions = useEditorActions();
  if (!group || group.selected.size < 2) return null;
  return (
    <div
      className="absolute left-1/2 top-2 z-40 -translate-x-1/2"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={() => {
          actions.groupFreeformElements(slideId, rowId, columnId, [...group.selected]);
          group.clear();
        }}
        className="rounded-md bg-accent px-3 py-1 text-xs font-semibold text-white shadow-floating hover:bg-accent-hover"
      >
        Group {group.selected.size} elements
      </button>
    </div>
  );
}
